/**
 * Catch a shader that fails to compile, and recover from it.
 *
 * luma.gl reports a failed compile by injecting a full-screen HTML panel with
 * the shader source and the errors. It is a DOM node, not a console message,
 * which is why watching the console missed it twice.
 *
 * That failure mode is unacceptable for a decorative feature: a broken facade
 * shader takes the whole building layer with it, and the user is left staring
 * at a wall of GLSL over a blank map. So watch for the panel, and if it
 * appears, remove it and turn the offending feature off.
 *
 * This is a safety net, not a fix. It exists because shader compilation
 * depends on the driver, and a shader that compiles on one machine can fail on
 * another — which is exactly what happened here.
 */

/** Text that only appears in luma.gl's error panel. */
const MARKERS = ["MAIN SHADER SOURCE", "COPY SOURCE", "ERROR: "];

function looksLikeShaderError(node) {
  if (!(node instanceof HTMLElement)) return false;
  // Only consider things that cover the page — the panel is full-screen, and
  // we must never rip out a legitimate overlay of our own.
  if (node.id && ["map", "tip", "boot", "mark-editor"].includes(node.id)) return false;
  if (node.className && /panel|hud|fx-|topbar/.test(String(node.className))) return false;

  const text = node.innerText || "";
  return MARKERS.some((marker) => text.includes(marker));
}

/**
 * @param onFailure called with the panel's text when a shader error appears
 * @returns a stop() function
 */
export function watchForShaderErrors(onFailure) {
  if (typeof MutationObserver === "undefined") return () => {};

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!looksLikeShaderError(node)) continue;

        // Keep the text: it is the only record of what actually failed, and
        // guessing at it has already cost two rounds.
        const text = (node.innerText || "").slice(0, 4000);
        node.remove();
        onFailure(text);
        return;
      }
    }
  });

  observer.observe(document.body, { childList: true });
  return () => observer.disconnect();
}
